<template>
  <header
    ref="root"
    v-bind="$attrs"
    class="tw-flex tw-h-20 tw-items-center tw-justify-between tw-border-b-2 tw-border-purple/25 tw-py-2"
  >
    <div class="tw-hidden tw-items-center lg:tw-flex">
      <Link :href="nateUiUrl || '#'" scheme="noUnderline">
        <Heading level="h1" level-class="h2"> {{ stationName }} - NATE Runner </Heading>

        <Text v-if="trackName" class="tw-mt-1">
          {{ trackName }}
        </Text>
      </Link>
    </div>

    <nav class="tw-hidden tw-items-center tw-gap-3 tw-text-sm lg:tw-flex">
      <Link scheme="underline" text-size="large" :href="controlRoomUrl" target="_blank">
        {{ t('header_navigation.control_room.label') }}
      </Link>

      <div class="tw-w-0.5 tw-self-stretch tw-bg-purple/25"></div>

      <Link scheme="underline" text-size="large" href="https://crew.slideslive.com/RoboCam" target="_blank">
        {{ t('header_navigation.about') }}
      </Link>

      <div class="tw-w-0.5 tw-self-stretch tw-bg-purple/25"></div>

      <Link href="#" scheme="underline" text-size="large" @click.prevent="openOnCallSupport">
        {{ t('header_navigation.on_call_support') }}
      </Link>

      <div class="tw-w-0.5 tw-self-stretch tw-bg-purple/25"></div>

      <Text size="large" v-tooltip="changeLogTooltip" class="tw-font-black">
        {{ runnerVersion || t('header_navigation.unknown_version') }}
      </Text>
    </nav>

    <div class="tw-flex tw-w-full tw-items-center tw-justify-between tw-space-x-2 lg:tw-w-auto">
      <Drawer
        class="lg:tw-hidden"
        :trigger-attrs="{
          icon: 'menu',
          shape: 'rounded',
          color: 'semiTransparentWhite',
          iconButton: true,
        }"
      >
        <div class="tw-mt-2 tw-flex tw-flex-col tw-gap-2 tw-px-2 tw-py-1 lg:tw-mt-0">
          <Link scheme="noUnderline" :href="controlRoomUrl" textSize="large" target="_blank">
            {{ t('header_navigation.control_room.label') }}
          </Link>

          <Link scheme="noUnderline" href="https://crew.slideslive.com/RoboCam" textSize="large" target="_blank">
            {{ t('header_navigation.about') }}
          </Link>

          <Link href="#" scheme="noUnderline" textSize="large" @click.prevent="openOnCallSupport">
            {{ t('header_navigation.on_call_support') }}
          </Link>

          <Link href="#" scheme="underline" text-size="large" v-tooltip="changeLogTooltip">
            {{ runnerVersion || t('header_navigation.unknown_version') }}
          </Link>
        </div>

        <template #footer>
          <div class="tw-flex tw-w-full tw-items-center tw-justify-between">
            <div>
              <Heading level="h5"> {{ stationName }} - NATE Runner </Heading>

              <Text v-if="trackName" class="tw-mt-1">
                {{ trackName }}
              </Text>
            </div>

            <Button
              :href="nateUiUrl || '#'"
              tag="a"
              size="small"
              :disabled="!canGoToNate"
              @click="onGoToNateClick"
            >
              {{ t('header_navigation.go_to_nate') }}
            </Button>
          </div>
        </template>
      </Drawer>

      <div class="tw-hidden tw-items-center lg:tw-flex">
        <Button :href="nateUiUrl || '#'" tag="a" :disabled="!canGoToNate" @click="onGoToNateClick">
          {{ t('header_navigation.go_to_nate') }}
        </Button>
      </div>

      <Logo class="tw-block tw-w-28 lg:tw-hidden" />
    </div>
  </header>

  <OnCallSupportModal v-model="onCallSupportOpen" />
</template>

<script setup>
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { Button, Drawer, Heading, Link, Logo, Text } from '@slideslive/fuse-kit/vue';

import useStateStore from '@/stores/state';
import { versionLabel } from '@/utils/sidebar_status';
import toBrowserHost from '@/utils/station_url';

import OnCallSupportModal from './OnCallSupportModal.vue';

const { t } = useI18n();

const onCallSupportOpen = ref(false);

const stateStore = useStateStore();
const { state } = storeToRefs(stateStore);

const data = computed(() => state.value?.data || {});

const runnerVersion = computed(() => data.value?.app?.version || null);

const nate = computed(() => data.value?.nate || {});
const nateImage = computed(() => nate.value?.image || {});

const nateStatus = computed(() => nate.value?.status || '');
const nateUiUrl = computed(() => toBrowserHost(nate.value?.ui_url?.trim(), data.value?.app?.host_url));

const canGoToNate = computed(() => nateStatus.value === 'running' && Boolean(nateUiUrl.value));

const stationName = computed(() => nate.value?.station_name?.trim() || '');
const trackName = computed(() => nate.value?.track_name?.trim() || '');

const controlRoomUrl = computed(() => {
  const base = 'http://robocam.slideslive.dev/';
  const number = stationName.value.replace(/^#/, '');
  if (!number) return base;
  return `${base}?filter=${number}`;
});

const nateVersionLabel = computed(() => versionLabel(nateImage.value));

const changeLogTooltip = computed(() => ({
  disabled: false,
  html: true,
  content: `
    <div>
      <div><strong>NATE:</strong> ${nateVersionLabel.value}</div>
      <div><strong>NATE Runner:</strong> ${runnerVersion.value}</div>
      <div><strong>NATE Runner UI:</strong> ${import.meta.env.PACKAGE_VERSION}</div>
    </div>
  `,
}));

function onGoToNateClick(event) {
  if (canGoToNate.value) return;
  if (event) event.preventDefault();
}

function openOnCallSupport() {
  onCallSupportOpen.value = true;
}
</script>
